import {
  Skeleton,
  SkeletonImage,
  SkeletonRegion,
  SkeletonShelf,
  SkeletonTable,
} from '@/components/common/Skeleton';

/**
 * Route-level fallbacks for `loadable()`.
 *
 * A lazy chunk arriving is the FIRST wait a player sees, before any API call
 * starts, and the shared spinner made every route look identical while it
 * happened. These give the chunk boundary the shape of the page behind it, so
 * the layout does not jump when the real component mounts.
 *
 * They are deliberately coarse: their job is to hold the page's shape for a few
 * hundred milliseconds, not to mirror it element for element — that is the
 * per-page skeletons' job once the data request is in flight.
 */

/** Home and the casino lobby: a banner, a tile grid, then shelves. */
export const LobbyRouteSkeleton = () => (
  <SkeletonRegion label="Loading page" className="w-full pb-10">
    <div className="px-4 pt-3">
      <SkeletonImage height={132} />
    </div>
    <div className="grid grid-cols-4 gap-2 px-4 py-3">
      {Array.from({ length: 8 }, (_, i) => (
        <Skeleton key={i} height={70} radius="var(--cb-radius-s, .5rem)" />
      ))}
    </div>
    <SkeletonShelf />
    <SkeletonShelf />
  </SkeletonRegion>
);

/** The sports board: toolbar, sport strip, then competition blocks. */
export const BoardRouteSkeleton = () => (
  <SkeletonRegion label="Loading page" className="w-full p-2.5 pb-24">
    <Skeleton height={50} radius="var(--cb-radius-m, .75rem)" />
    <Skeleton height={46} radius="var(--cb-radius-m, .75rem)" className="mt-2.5" />
    <Skeleton height={62} radius="var(--cb-radius-m, .75rem)" className="mt-2.5" />
    <div className="mt-3 flex flex-col gap-3">
      {Array.from({ length: 3 }, (_, i) => (
        <Skeleton key={i} height={150} radius="var(--cb-radius-m, .75rem)" />
      ))}
    </div>
  </SkeletonRegion>
);

/** Statements, histories and any other report page built on a table. */
export const TableRouteSkeleton = () => (
  <SkeletonRegion label="Loading page" className="w-full p-4 pb-24">
    <Skeleton width={180} height={20} radius={999} />
    <div className="mt-4 flex gap-2">
      <Skeleton width={120} height={38} />
      <Skeleton width={120} height={38} />
    </div>
    <div className="mt-4 overflow-hidden rounded-cb-m bg-white">
      <SkeletonTable rows={7} />
    </div>
  </SkeletonRegion>
);

/** Profile, settings and the other stacked-card forms. */
export const FormRouteSkeleton = () => (
  <SkeletonRegion label="Loading page" className="mx-auto w-full max-w-[560px] p-3 pb-24">
    {[3, 4, 3].map((rows, c) => (
      <div key={c} className="mb-3 rounded-cb-m bg-cb-surface p-3.5">
        <Skeleton width={130} height={15} radius={999} />
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} height={42} radius="var(--cb-radius-s, .5rem)" className="mt-3" />
        ))}
      </div>
    ))}
  </SkeletonRegion>
);
